'use client';
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { FiAlertTriangle, FiX } from 'react-icons/fi';

export default function ImpersonationIndicator() {
  const { user } = useAuth();
  const [isStopping, setIsStopping] = useState(false);

  // Only show if user is being impersonated
  if (!user?.isImpersonated) {
    return null;
  }

  const stopImpersonation = async () => {
    try {
      setIsStopping(true);
      const response = await fetch('/api/ghackk-admin/stop-impersonation', {
        method: 'POST',
        credentials: 'include', // Include cookies
      });

      if (response.ok) {
        // Redirect back to admin page to clear the impersonation state
        window.location.href = '/ghackk-admin';
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Failed to stop impersonation:', errorData.error);
        
        // If the API fails, try a fallback approach - clear cookies manually and redirect
        if (errorData.error.includes('Admin access required') || errorData.error.includes('Authentication required')) {
          // Clear cookies manually and redirect
          document.cookie = 'impersonation_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
          window.location.href = '/ghackk-admin';
          return;
        }
        
        // Show user-friendly error message
        alert(`Failed to stop impersonation: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      
      // Fallback: Clear cookies manually and redirect
      document.cookie = 'impersonation_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      window.location.href = '/ghackk-admin';
    } finally {
      setIsStopping(false);
    }
  };

  return (
    <div className="sticky top-0 z-50 bg-yellow-600 border-b border-yellow-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-1.5">
          <div className="flex items-center">
            <FiAlertTriangle className="h-4 w-4 text-yellow-200 mr-2" />
            <p className="text-xs font-medium text-yellow-200">
              Viewing as{' '}
              <span className="font-bold text-white">{user.name}</span>
              {user.email && ` (${user.email})`}
            </p>
          </div>
          <button
            onClick={stopImpersonation}
            disabled={isStopping}
            className="inline-flex items-center px-2 py-0.5 border border-transparent text-xs font-medium rounded text-yellow-600 bg-yellow-200 hover:bg-yellow-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isStopping ? (
              <div className="animate-spin rounded-full h-2.5 w-2.5 border-b-2 border-yellow-600 mr-1"></div>
            ) : (
              <FiX className="mr-1 h-3 w-3" />
            )}
            {isStopping ? 'Stopping...' : 'Stop'}
          </button>
        </div>
      </div>
    </div>
  );
} 